<script>
import { mapGetters, mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import DeleteConfirmationModal from "../../branches/components/deleteConfirmationModal.vue";
import ReassignStaffModal from "./reassignStaffModal.vue"
import UpdateStatusModal from './updateStatusModal.vue';
import RescheduleModal from './rescheduleModal.vue';
import EditStaffNotesModal from './editStaffNotesModal.vue';

export default{
    components: {
        BackButton,
        ReassignStaffModal,
        DeleteConfirmationModal,
        UpdateStatusModal,
        RescheduleModal,
        EditStaffNotesModal,
    },
    data(){
        return{
            appointmentId: this.$route.params.appointmentId,
            appointment: null,
            isLoading: true,
            error: null,
            isReassignStaff: false,
            deleteObject: null,
            isUpdateStatus: false,
            isReschedule: false,
            isEditStaffNote: false,
        }
    },
    computed: {
        ...mapGetters('appointments', ['selectedBranch']),

        backTarget() {
            const conversationId = this.appointment?.conversation_id;
            if (conversationId) {
                return {
                    name: 'inbox_conversation',
                    params: {
                        accountId: this.$store.getters.getCurrentAccountId,
                        conversation_id: conversationId,
                    },
                };
            }
            return { name: 'appointments' };
        },

        isServiceDeleted() {
            return this.appointment?.service?.is_deleted || false;
        },

        hasClientLocalTime() {
            const clientTz = this.appointment?.client_time_info?.client_timezone;
            return !!clientTz && clientTz !== this.appointment?.timezone && !!this.appointment?.client_time_info?.date;
        },

        // Computed property to determine status color
        statusColorClass() {
            if (!this.appointment) return '';
            
            const status = this.appointment.status.toLowerCase();
            switch (status) {
                case 'cancelled':
                case 'archive':
                    return 'bg-red-100 text-red-800';
                case 'confirmed':
                    return 'bg-green-100 text-green-800';
                case 'rescheduled':
                    return 'bg-woot-100 text-woot-800';
                case 'upcoming':
                    return 'bg-teal-100 text-teal-800';
                case 'requested':
                    return 'bg-orange-100 text-orange-800';
                default:
                    return 'bg-slate-100 text-slate-800';
            }
        }
    },
    methods:{
        ...mapActions('appointments', [
            // 'fetchAppointment',
            // 'deleteAppointment',
            'fetchAppointments'
        ]),

        ...mapActions('appointmentsV2', [
            'deleteAppointment',
            'fetchAppointment'
        ]),
        
        formatClientTime(time) {
            return time ? time.slice(0, 5) : '';
        },

        async loadAppointmentData() {
            try {
                this.isLoading = true;
                this.error = null;
                this.appointment = await this.fetchAppointment(this.appointmentId);
            } catch(err) {
                console.error('Error loading appointment details:', err);
                this.error = err.message || 'Failed to load appointment details';
                useAlert(this.error);
            } finally {
                this.isLoading = false;
            }
        },
        
        resetModal() {
            this.isReassignStaff = false;
            this.isEditStaffNote = false;
            this.isUpdateStatus = false;
            this.isReschedule = false;
            this.deleteObject = null;
        },
        
        async handleDeleteAppointment(id) {
            try {
                await this.deleteAppointment({ accountId: this.$store.getters['getCurrentAccountId'], appointmentId: id });
                useAlert('Appointment deleted successfully');
                this.$router.push(this.backTarget);
            } catch(err) {
                console.error('Error deleting appointment:', err);
                useAlert(err.message || 'Failed to delete appointment');
            } finally { 
                this.deleteObject = null;
            }
        },
        
        async refreshDetails() {
            await this.loadAppointmentData();
        },
        
        goBack() {
            this.$router.push(this.backTarget);
        },
    },
    async mounted() {
        await this.loadAppointmentData();
    }
}
</script>

<template>
    <div data-testid="view_appointment_page" class="w-full h-full flex flex-col overflow-hidden">
        <div class="flex-shrink-0 flex items-center h-14 min-h-[3.5rem] px-4 py-2 bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800/50">
            <h1 class="flex items-center mb-0 text-2xl text-slate-900 dark:text-slate-100">
                <BackButton data-testid="view_appointment_back_button" class="ml-2 mr-4" :back-url="backTarget" />
                <fluent-icon icon="calendar-clock" class="hidden ml-1 mr-2 rtl:ml-2 rtl:mr-1 md:block" />
                <span class="text-2xl font-medium text-slate-900 dark:text-slate-100">
                    Appointment Details
                </span>
            </h1>
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto">
        <!-- Loading State with Enhanced Animation -->
        <div v-if="isLoading" class="flex flex-col items-center justify-center p-8 transition-all duration-300 ease-in-out">
            <div class="relative w-16 h-16">
                <div class="w-16 h-16 border-4 border-woot-500 border-t-transparent rounded-full animate-spin"></div>
                <div class="absolute inset-0 flex items-center justify-center">
                    <fluent-icon icon="calendar" size="20" class="text-woot-600 animate-pulse" />
                </div>
            </div>
            <p class="mt-4 text-slate-600 dark:text-slate-300 animate-pulse">Loading appointment details...</p>
        </div>
        
        <!-- Error State with Better Error Information -->
        <div v-else-if="error" data-testid="view_appointment_error" class="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg shadow-sm transition-all duration-300 ease-in-out">
            <div class="flex items-start gap-3">
                <fluent-icon icon="error-circle" size="24" class="text-red-600 dark:text-red-400 flex-shrink-0 mt-1" />
                <div>
                    <h3 class="text-lg font-medium text-red-700 dark:text-red-300 mb-2">Unable to load appointment</h3>
                    <p class="text-red-600 dark:text-red-400 mb-4">{{ error }}</p>
                    <div class="flex gap-3">
                        <woot-button
                            variant="clear"
                            color-scheme="secondary"
                            size="small"
                            icon="chevron-left"
                            @click="goBack"
                            aria-label="Return to appointment list"
                        >
                            Back to List
                        </woot-button>
                        <woot-button
                            variant="clear"
                            color-scheme="alert"
                            size="small"
                            icon="arrow-clockwise"
                            @click="loadAppointmentData"
                            aria-label="Try loading the appointment again"
                        >
                            Retry
                        </woot-button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Appointment Details with Enhanced Layout -->
        <div v-else-if="appointment" class="flex flex-col bg-white dark:bg-slate-800 rounded-lg transition-all duration-300 ease-in-out">
            <!-- Modals -->
            <div
                v-if="isReassignStaff === true"
                class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
            >
                <ReassignStaffModal
                    :serviceObject="appointment.service" 
                    :appointmentId="appointmentId" 
                    @resetModal="resetModal()" 
                    @refreshDetails="refreshDetails"
                />
            </div>

            <div
                v-if="isEditStaffNote === true"
                class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
            >
                <EditStaffNotesModal
                    :staff_notes="appointment.staff_notes" 
                    :appointmentId="appointmentId" 
                    @resetModal="isEditStaffNote = false" 
                    @refreshDetails="refreshDetails"
                />
            </div>

            <div
                v-if="deleteObject"
                class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
            >
                <DeleteConfirmationModal        
                    :deleteName="deleteObject.name"
                    @confirm-delete="handleDeleteAppointment(deleteObject.id)"
                    @close="deleteObject = null"
                />
            </div>

            <div
                v-if="isReschedule"
                class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
            >
                <RescheduleModal        
                    @closeModal="isReschedule = false" 
                    :appointmentId="appointmentId"
                    :service="appointment.service"
                    :branch="appointment.branch"
                    :staff="appointment.staff"
                    :previousTime="appointment.start_time"
                    :previousDate="appointment.date"
                    :client_time_info="appointment.client_time_info"
                    :timezone="appointment.timezone"
                    @refreshDetails="refreshDetails"
                />
            </div>

            <UpdateStatusModal 
                v-if="isUpdateStatus" 
                @closeModal="isUpdateStatus = false" 
                :status="appointment.status" 
                :appointmentId="appointmentId" 
                @refreshDetails="refreshDetails"
            />

            <!-- Header Card with Contact Info and Status -->
            <div class="flex flex-col md:flex-row gap-4 p-6 border-b border-slate-100 dark:border-slate-700">
                <div class="flex items-center gap-4 md:w-2/3">
                    <!-- Contact Icon with Avatar Background -->
                    <div class="w-16 h-16 rounded-full bg-woot-50 dark:bg-woot-900/30 flex items-center justify-center flex-shrink-0">
                        <fluent-icon icon="people" size="32" class="text-woot-600 dark:text-woot-400" />
                    </div>

                    <!-- Contact Info -->
                    <div class="flex flex-col">
                        <h2 data-testid="view_appointment_contact_name" class="text-xl font-bold mb-1" :title="appointment.contact.name">
                            {{ appointment.contact.name }}
                        </h2>
                        <div class="flex items-center gap-2 mb-2">
                            <span 
                                data-testid="view_appointment_status"
                                class="px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors"
                                :class="statusColorClass"
                                aria-label="Appointment status"
                            >
                                {{ appointment.status }}
                            </span>
                            <span class="text-xs text-slate-500 dark:text-slate-400">
                                ID: {{ appointmentId }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Action Buttons Group -->
                <div class="flex flex-wrap gap-2 md:ml-auto items-start">
                    <!-- Constructive Actions -->
                    <div class="flex flex-wrap gap-2">
                        <woot-button
                            variant="smooth"
                            color-scheme="secondary"
                            size="small"
                            icon="status"
                            @click="isUpdateStatus = true"
                            aria-label="Update appointment status"
                        >
                            Update Status
                        </woot-button>

                        <woot-button
                            variant="smooth"
                            color-scheme="primary"
                            size="small"
                            icon="calendar"
                            @click="isReschedule = true"
                            aria-label="Reschedule this appointment"
                        >
                            Reschedule
                        </woot-button>
                    </div>

                    <!-- Destructive Actions (Separated visually) -->
                    <woot-button
                        variant="smooth"
                        color-scheme="alert"
                        size="small"
                        icon="delete"
                        @click="deleteObject = { name: `${appointment.contact.name}'s appointment`, id: appointmentId }"
                        aria-label="Delete this appointment"
                        class="ml-auto md:ml-2"
                    >
                        Delete
                    </woot-button>
                </div>
            </div>

            <!-- Main Content Area -->
            <div class="p-6">
                <!-- Appointment Details Section -->
                <div class="mb-8">
                    <h3 class="text-lg font-bold mb-4 text-slate-800 dark:text-slate-200 border-b border-slate-100 dark:border-slate-700 pb-2">
                        Appointment Details
                    </h3>
                    
                    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                        <!-- Date and Time Information -->
                        <div class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
                            <div class="flex items-start mb-2">
                                <fluent-icon icon="calendar" size="20" class="text-woot-600 dark:text-woot-400 mr-2 mt-0.5" />
                                <h4 class="font-semibold text-slate-700 dark:text-slate-300">
                                    {{ appointment.before_reschedule_metadata ? 'Current Schedule' : 'Date and Time' }}
                                </h4>
                            </div>
                            <p class="text-slate-600 dark:text-slate-400 ml-7">
                                {{ appointment.date }}, {{ appointment.start_time }}
                            </p>
                            <p
                                v-if="hasClientLocalTime"
                                class="text-xs text-slate-400 dark:text-slate-500 ml-7 mt-1 truncate"
                                :title="`${$t('APPOINTMENTS.APPOINTMENT_DETAILS.CUSTOMER_LOCAL_TIME')}: ${appointment.client_time_info.date}, ${formatClientTime(appointment.client_time_info.start_time)} (${appointment.client_time_info.client_timezone})`"
                            >
                                {{ $t('APPOINTMENTS.APPOINTMENT_DETAILS.CUSTOMER_LOCAL_TIME') }}: {{ appointment.client_time_info.date }}, {{ formatClientTime(appointment.client_time_info.start_time) }} ({{ appointment.client_time_info.client_timezone }})
                            </p>
                        </div>

                        <!-- Original Schedule (if rescheduled) -->
                        <div v-if="appointment.before_reschedule_metadata" class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
                            <div class="flex items-start mb-2">
                                <fluent-icon icon="clock" size="20" class="text-amber-600 dark:text-amber-400 mr-2 mt-0.5" />
                                <h4 class="font-semibold text-slate-700 dark:text-slate-300">Original Schedule</h4>
                            </div>
                            <p class="text-slate-600 dark:text-slate-400 ml-7">
                                {{ appointment.before_reschedule_metadata.date }}, 
                                {{ appointment.before_reschedule_metadata.start_time }}
                            </p>
                        </div>
                        
                        <!-- Service Information -->
                        <div class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
                            <div class="flex items-start mb-2">
                                <fluent-icon icon="briefcase" size="20" class="text-woot-600 dark:text-woot-400 mr-2 mt-0.5" />
                                <h4 class="font-semibold text-slate-700 dark:text-slate-300">Service</h4>
                            </div>
                            <div class="flex items-center gap-2 ml-7 mb-4">
                                <p class="text-slate-600 dark:text-slate-400 mb-0">
                                    {{ appointment.service.service_name }}
                                </p>
                                <fluent-icon 
                                    v-if="isServiceDeleted"
                                    v-tooltip.top="'This service has been deleted'"
                                    icon="warning" 
                                    size="18"
                                    class="text-amber-500 dark:text-amber-400 flex-shrink-0"
                                />
                            </div>
                        </div>
                        
                        <!-- Branch Information -->
                        <div class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
                            <div class="flex items-start mb-2">
                                <fluent-icon icon="building-bank" size="20" class="text-woot-600 dark:text-woot-400 mr-2 mt-0.5" />
                                <h4 class="font-semibold text-slate-700 dark:text-slate-300">Branch</h4>
                            </div>
                            <p class="text-slate-600 dark:text-slate-400 ml-7">
                                {{ appointment.branch.branch_name }}
                            </p>
                        </div>
                        
                        <!-- Created By Information -->
                        <div class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
                            <div class="flex items-start mb-2">
                                <fluent-icon icon="person" size="20" class="text-woot-600 dark:text-woot-400 mr-2 mt-0.5" />
                                <h4 class="font-semibold text-slate-700 dark:text-slate-300">Created By</h4>
                            </div>
                            <p class="text-slate-600 dark:text-slate-400 ml-7">
                                {{ appointment.created_by ? appointment.created_by : "—" }}
                            </p>
                        </div>

                        <!-- Reminder placeholder -->
                        <div class="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                            <div class="flex items-start mb-1.5">
                                <fluent-icon icon="alarm" size="16" class="text-woot-600 dark:text-woot-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                <h4 class="font-semibold text-sm text-slate-700 dark:text-slate-300 truncate">Reminder</h4>
                            </div>
                            <p class="text-sm text-slate-600 dark:text-slate-400 ml-6 truncate" :title="created_by ? created_by : '-'">
                                30 minutes prior
                            </p>
                        </div>
                    </div>
                </div>
                
                <!-- Staff Assignment Section -->
                <div class="mb-8">
                    <h3 class="text-lg font-bold mb-4 text-slate-800 dark:text-slate-200 border-b border-slate-100 dark:border-slate-700 pb-2">
                        Staff Assignment
                    </h3>
                    <div class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
                        <div class="flex justify-between items-center">
                            <div class="flex items-center gap-3">
                                <div class="w-10 h-10 rounded-full bg-woot-50 dark:bg-woot-900/30 flex items-center justify-center flex-shrink-0">
                                    <fluent-icon icon="person" size="20" class="text-woot-600 dark:text-woot-400" />
                                </div>
                                <div>
                                    <p class="font-medium">{{ appointment.staff.staff_name }}</p>
                                    <p class="text-sm text-slate-500 dark:text-slate-400">Assigned Staff</p>
                                </div>
                            </div>
                            <woot-button
                                variant="smooth"
                                color-scheme="primary"
                                size="small"
                                icon="arrow-swap"
                                @click="isReassignStaff = true"
                                aria-label="Reassign this appointment to another staff member"
                            >
                                Reassign
                            </woot-button>
                        </div>
                    </div>
                </div>
                
                <!-- Staff Notes Section -->
                <div>
                    <div class="flex justify-between items-center mb-4 border-b border-slate-100 dark:border-slate-700 pb-2">
                        <h3 class="text-lg font-bold text-slate-800 dark:text-slate-200">
                            Staff Notes
                        </h3>
                        <woot-button
                            variant="smooth"
                            color-scheme="primary"
                            size="small"
                            icon="edit"
                            @click="isEditStaffNote = true"
                            aria-label="Edit staff notes"
                        >
                            Edit Notes
                        </woot-button>
                    </div>
                    <div class="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg min-h-24">
                        <p v-if="appointment.staff_notes" class="whitespace-pre-wrap text-slate-700 dark:text-slate-300">
                            {{ appointment.staff_notes }}
                        </p>
                        <p v-else class="text-slate-500 dark:text-slate-400 italic">
                            There are no notes for this appointment.
                        </p>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Empty State with Better Visual Feedback -->
        <div v-else class="p-8 bg-white dark:bg-slate-800 rounded-lg shadow border border-slate-100 dark:border-slate-700 flex flex-col items-center justify-center text-center">
            <div class="w-20 h-20 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-4">
                <fluent-icon icon="warning" size="32" class="text-amber-500 dark:text-amber-400" />
            </div>
            <h3 class="text-xl font-medium text-slate-800 dark:text-slate-200 mb-2">Appointment Not Found</h3>
            <p class="text-slate-600 dark:text-slate-400 text-sm max-w-md mb-6">
                The appointment you're looking for doesn't exist or may have been deleted. Please check the appointment ID and try again.
            </p>
            <woot-button
                variant="clear"
                color-scheme="primary"
                size="small"
                icon="chevron-left"
                @click="goBack"
                aria-label="Return to appointment list"
            >
                Back to Appointments
            </woot-button>
        </div>
        </div>
    </div>
</template>

<style scoped>
/* Smooth transitions for all interactive elements */
button, 
a, 
.rounded-full,
.rounded-lg {
    transition: all 0.2s ease-in-out;
}

/* Enhance hover effects */
button:hover,
a:hover {
    transform: translateY(-1px);
}

button:active,
a:active {
    transform: translateY(0);
}
</style>